import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { message, Modal } from 'antd';
import communityAPI from '../../../api/community';
import Badge from '../../../componentes/ui/Badge';
import { useCommunityAuth } from '../../../contexts/CommunityAuthContext';
import { LearningPathActions } from '../shared/CRUDActions';
import { LearningPathEdit } from './LearningPathManager';
import { FullscreenSpinner, InlineSpinner } from '../../../componentes/LoadingIndicator';
import { formatNumber } from '../../../utils/numberFormat';

const PROFILE_STORAGE_KEY = 'community:last-profile';

export default function LearningDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useCommunityAuth();
  const [path, setPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPaths, setRelatedPaths] = useState([]);
  const [featuredPaths, setFeaturedPaths] = useState([]);
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [togglingLesson, setTogglingLesson] = useState(null);
  const [editingPath, setEditingPath] = useState(null);
  const storedProfileInfo = useMemo(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.sessionStorage.getItem(PROFILE_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);
  const fromAdmin = Boolean(location.state?.fromAdmin);
  const adminBackTo = location.state?.backTo || '/admin/content/learning-paths';
  const profileUsername = location.state?.fromProfile || storedProfileInfo?.username;
  const profilePath =
    location.state?.fromProfilePath ||
    storedProfileInfo?.path ||
    (profileUsername ? `/profile/${profileUsername}` : undefined);
  const isCommunityRoute = location.pathname.startsWith('/community/learning');

  const buildNavigationState = () => {
    const state = {};
    if (profileUsername) {
      state.fromProfile = profileUsername;
    }
    if (profilePath) {
      state.fromProfilePath = profilePath;
    }
    return Object.keys(state).length ? state : undefined;
  };

  const buildPathLink = (nextPath) => {
    const prefix = isCommunityRoute ? '/community/learning' : '/learning';
    const state = buildNavigationState();
    if (!nextPath) {
      return state ? { pathname: prefix, state } : prefix;
    }
    const suffix = nextPath.slug ? `/${nextPath.slug}` : `/${nextPath.id}`;
    const pathname = `${prefix}${suffix}`;
    return state ? { pathname, state } : pathname;
  };

  const buildPostLink = (post) => {
    const base = post ? (post.slug ? `/blog/${post.slug}` : `/blog/${post.id}`) : '/blog';
    const state = buildNavigationState();
    const shouldUsePublicContext = Boolean(profilePath) || !isCommunityRoute;
    const combinedState = {
      ...(state || {}),
      ...(shouldUsePublicContext ? { fromPublic: true } : {}),
    };
    const search = `?from=learning&pathId=${id}`;
    return Object.keys(combinedState).length
      ? { pathname: base, search, state: combinedState }
      : { pathname: base, search };
  };

  useEffect(() => {
    fetchPath();
  }, [id, user]);

  const fetchPath = async () => {
    setLoading(true);
    try {
      const res = await communityAPI.learningPaths.getById(id);
      setPath(res.data);

      // Fetch related paths
      try {
        const relatedRes = await communityAPI.learningPaths.getRelated(id);
        setRelatedPaths(Array.isArray(relatedRes.data) ? relatedRes.data : []);
      } catch (err) {
        console.error('Error fetching related paths:', err);
        setRelatedPaths([]);
      }

      // Fetch featured paths
      try {
        const featuredRes = await communityAPI.learningPaths.getFeatured();
        setFeaturedPaths(Array.isArray(featuredRes.data) ? featuredRes.data : []);
      } catch (err) {
        console.error('Error fetching featured paths:', err);
        setFeaturedPaths([]);
      }

      // Load completed lessons
      if (user) {
        try {
          const progressRes = await communityAPI.progress.getCompletedLessons(id);
          const completed = progressRes.data?.completed_lessons || [];
          setCompletedLessons(new Set(completed));
          localStorage.setItem(`completed_${id}`, JSON.stringify(completed));
        } catch (err) {
          console.error('Error fetching progress:', err);
          const completed = JSON.parse(localStorage.getItem(`completed_${id}`) || '[]');
          setCompletedLessons(new Set(completed));
        }
      } else {
        const completed = JSON.parse(localStorage.getItem(`completed_${id}`) || '[]');
        setCompletedLessons(new Set(completed));
      }
    } catch (err) {
      console.error('Error loading learning path:', err);
      message.error('Learning path not found');
      setPath(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleLessonComplete = async (postId) => {
    if (!user) {
      message.error('Please login to track progress');
      return;
    }

    setTogglingLesson(postId);
    try {
      const res = await communityAPI.progress.toggleComplete(postId, id);
      const updated = new Set(completedLessons);
      if (res.data?.completed) {
        updated.add(postId);
      } else {
        updated.delete(postId);
      }
      setCompletedLessons(updated);
      localStorage.setItem(`completed_${id}`, JSON.stringify([...updated]));
    } catch (err) {
      console.error('Failed to toggle completion:', err);
      message.error('Failed to update progress');
    } finally {
      setTogglingLesson(null);
    }
  };

  const progress = path?.posts?.length ? (completedLessons.size / path.posts.length) * 100 : 0;
  const isAuthor = user && path && (user.id === path.author?.id);
  const fallbackListTarget = fromAdmin
    ? adminBackTo
    : isCommunityRoute
    ? '/community/learning'
    : '/learning';
  const backTarget = profilePath || (fromAdmin ? adminBackTo : isAuthor ? '/community/learning' : fallbackListTarget);
  const backLabel = fromAdmin
    ? 'Back to admin'
    : profilePath
    ? 'Back to Profile'
    : isAuthor
    ? 'Back to My Learning Paths'
    : 'Back to Learning Paths';

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0E0E0E] pt-24 blog-body">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <FullscreenSpinner message="Loading learning path..." tone="slate" />
        </div>
      </div>
    );
  }

  if (!path) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <div className="text-red-600 dark:text-red-400 text-xl">Learning path not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#0E0E0E] pt-24 blog-body">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@400;500;600&display=swap');
        .blog-title { font-family: 'Crimson Pro', Georgia, serif; }
        .blog-body { font-family: 'DM Sans', sans-serif; }
      `}</style>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3">
            <Link
              to={backTarget}
              className="inline-flex items-center gap-2 text-primary dark:text-primary/80 hover:text-[#229bb8] dark:hover:text-primary/70 font-medium mb-8 transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {backLabel}
            </Link>

            <div className="bg-white dark:bg-[#161616] rounded-3xl shadow-2xl overflow-hidden">
              <div className="p-8 lg:p-12 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h1 className="blog-title text-4xl lg:text-5xl font-bold text-gray-950 dark:text-white mb-4">{path.title}</h1>
                    <div className="flex items-center gap-4 text-gray-600 dark:text-gray-300 mb-4">
                      <span>by {path.author?.username ? (
                        <Link to={`/profile/${path.author.username}`} className="hover:text-primary dark:hover:text-primary/80 transition-colors">
                          {path.author.username}
                        </Link>
                      ) : (
                        'Anonymous'
                      )}</span>
                      <span>•</span>
                      <span>{formatNumber(path.posts?.length || 0)} lessons</span>
                    </div>
                  </div>
                  
                  {isAuthor && (
                    <div className="ml-4">
                      <LearningPathActions 
                        learningPath={path}
                        onEdit={() => setEditingPath(path)}
                        onDeleted={() => navigate(backTarget)}
                      />
                    </div>
                  )}

                  <Badge color={path.level === 'beginner' ? 'green' : path.level === 'intermediate' ? 'yellow' : 'red'}>
                    {path.level || 'Unknown'}
                  </Badge>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">{path.description}</p>

                {user && path.posts?.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Your Progress</span>
                      <span className="text-sm font-semibold text-primary dark:text-primary/80">{Math.round(progress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-primary to-secondary h-full rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {formatNumber(completedLessons.size)} of {formatNumber(path.posts.length)} lessons completed
                    </p>
                  </div>
                )}
              </div>

              <div className="p-8 lg:p-12">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Course Content</h3>
                <div className="space-y-3">
                  {path.posts && path.posts.length ? (
                    path.posts.map((post, idx) => (
                      <div key={post.id} className="group">
                        <Link
                          to={buildPostLink(post)}
                          className="flex items-center gap-4 p-5 bg-gray-50 dark:bg-gray-700/30 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl transition-all duration-200 border border-transparent hover:border-primary/30 dark:hover:border-indigo-800"
                        >
                          <div
                            className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 ${
                              completedLessons.has(post.id)
                                ? 'bg-green-500'
                                : 'bg-gradient-to-br from-primary to-secondary'
                            }`}
                          >
                            {completedLessons.has(post.id) ? (
                              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              idx + 1
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 dark:text-white group-hover:text-primary dark:group-hover:text-primary/80 transition-colors mb-1">
                              {post.title}
                            </h4>
                            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                              <span className="capitalize">{post.post_type}</span>
                              {post.category && (
                                <>
                                  <span>•</span>
                                  <span>{post.category.name}</span>
                                </>
                              )}
                            </div>
                          </div>
                          {user && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                toggleLessonComplete(post.id);
                              }}
                              disabled={togglingLesson === post.id}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                                completedLessons.has(post.id)
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                              }`}
                            >
                              {togglingLesson === post.id ? (
                                <InlineSpinner
                                  tone="slate"
                                  message="Saving..."
                                  messageClassName={completedLessons.has(post.id)
                                    ? 'text-green-700 dark:text-green-400'
                                    : 'text-gray-700 dark:text-gray-300'}
                                />
                              ) : (
                                completedLessons.has(post.id) ? 'Completed' : 'Mark Complete'
                              )}
                            </button>
                          )}
                        </Link>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12 text-gray-600 dark:text-gray-400">
                      <svg
                        className="w-16 h-16 mx-auto mb-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                      </svg>
                      <p className="text-lg font-medium">No lessons available yet</p>
                      <p className="text-sm mt-2">Check back later for new content</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="space-y-6 sticky top-8">
              <div className="bg-white dark:bg-[#161616] rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Featured Paths</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Popular learning paths</p>
                </div>
                <div className="space-y-3 p-4">
                  {featuredPaths.length ? (
                    featuredPaths.map(p => (
                      <Link key={p.id} to={buildPathLink(p)} className="block group">
                        <div className="space-y-2">
                          <h4 className="font-semibold text-sm text-gray-900 dark:text-white group-hover:text-primary dark:group-hover:text-primary/80 transition-colors duration-200 line-clamp-2">
                            {p.title}
                          </h4>
                          <div className="flex items-center justify-between">
                            <Badge
                              color={p.level === 'beginner' ? 'green' : p.level === 'intermediate' ? 'yellow' : 'red'}
                              className="text-xs py-0"
                            >
                              {p.level || 'Unknown'}
                            </Badge>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{formatNumber(p.posts?.length || 0)} lessons</span>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="text-center py-6 text-xs text-gray-500 dark:text-gray-400">No featured paths yet</div>
                  )}
                </div>
              </div>

              <div className="bg-white dark:bg-[#161616] rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Related Paths</h3>
                </div>
                <div className="space-y-3 p-4">
                  {relatedPaths.length ? (
                    relatedPaths.map(p => (
                      <Link key={p.id} to={buildPathLink(p)} className="block group">
                        <div className="space-y-2">
                          <h4 className="font-medium text-sm text-gray-900 dark:text-white group-hover:text-primary dark:group-hover:text-primary/80 transition-colors duration-200 line-clamp-2">
                            {p.title}
                          </h4>
                          <div className="flex items-center justify-between">
                            <Badge
                              color={p.level === 'beginner' ? 'green' : p.level === 'intermediate' ? 'yellow' : 'red'}
                              className="text-xs py-0"
                            >
                              {p.level || 'Unknown'}
                            </Badge>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{formatNumber(p.posts?.length || 0)} lessons</span>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="text-center py-6 text-xs text-gray-500 dark:text-gray-400">No related paths found</div>
                  )}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Edit Learning Path Modal */}
      <Modal
        className="[&_.ant-modal-content]:dark:bg-[#161616] [&_.ant-modal-header]:dark:bg-[#161616] [&_.ant-modal-header]:dark:border-gray-800 [&_.ant-modal-title]:dark:text-white [&_.ant-modal-title]:blog-title [&_.ant-modal-title]:text-2xl [&_.ant-modal-close]:dark:text-gray-400 [&_.ant-modal-close:hover]:dark:text-white [&_.ant-modal-footer]:dark:bg-[#161616] [&_.ant-modal-footer]:dark:border-gray-800"
        open={!!editingPath}
        onCancel={() => setEditingPath(null)}
        footer={null}
        title="Edit Learning Path"
        width={800}
      >
        {editingPath && (
          <LearningPathEdit
            id={editingPath.id}
            onSave={() => {
                setEditingPath(null);
                fetchPath();
            }}
            onCancel={() => setEditingPath(null)}
          />
        )}
      </Modal>
    </div>
  );
}
